import { Fuel } from "lucide-react";
import type { FuelEfficiency } from "@/lib/expenses";
import { formatMoney } from "@/lib/format";

// Cost per km between odometer-stamped fill-ups. Hidden entirely until two
// fills carry a reading — a half-filled metric is worse than none.
export function FuelCost({ fuel }: { fuel: FuelEfficiency }) {
  if (fuel.rollingCentsPerKm == null) return null;

  const { currency, rollingCentsPerKm, rollingLegs, rollingKm, centsPerKm, lastLeg } =
    fuel;
  const perKm = (cents: number) => `${formatMoney(cents, currency)}/km`;

  return (
    <div className="rounded-[1.75rem] bg-card p-5 shadow-sm ring-1 ring-black/[0.05]">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex size-8 items-center justify-center rounded-full bg-red-100 text-red-700">
          <Fuel className="size-4" />
        </span>
        <h2 className="text-sm font-semibold">Fuel cost</h2>
      </div>

      <p className="text-[2rem] font-bold leading-none tabular-nums">
        {perKm(rollingCentsPerKm)}
      </p>
      <p className="mt-1.5 text-xs text-muted-foreground">
        Rolling average over the last {rollingLegs}{" "}
        {rollingLegs === 1 ? "fill" : "fills"} · {rollingKm.toLocaleString()} km
      </p>

      <div className="mt-4 grid grid-cols-2 gap-2 border-t border-border/70 pt-3">
        <div>
          <p className="text-xs text-muted-foreground">All time</p>
          <p className="mt-0.5 text-sm font-semibold tabular-nums">
            {centsPerKm != null ? perKm(centsPerKm) : "—"}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Last leg</p>
          <p className="mt-0.5 text-sm font-semibold tabular-nums">
            {lastLeg
              ? `${lastLeg.km.toLocaleString()} km · ${formatMoney(lastLeg.costCents, currency)}`
              : "—"}
          </p>
        </div>
      </div>
    </div>
  );
}
